import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db, auth } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/ClientShared.css";

const BrandQuestionnaire = () => {
  // const navigate = useNavigate();

  const [formData, setFormData] = useState({
    businessName: "",
    businessSummary: "",
    businessStory: "",
    customerProblems: "",
    uniqueSellingPoints: "",
    businessGoals: "",
    idealCustomer: "",
    audienceChallenges: "",
    audienceEmotions: "",
    competitors: "",
    preferredColors: "",
    preferredFonts: "",
    brandStyle: "",
    brandInspiration: "",
    additionalNotes: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [projectId, setProjectId] = useState(null);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        if (!userData.projectId) throw new Error("Project ID not found.");

        setProjectId(userData.projectId);

        const projectRef = doc(db, "projects", userData.projectId);
        const projectSnap = await getDoc(projectRef);

        if (projectSnap.exists()) {
          const projectData = projectSnap.data();
          if (projectData.brandQuestionnaire) {
            setFormData(projectData.brandQuestionnaire);
          }
        }
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load data.");
      }
    };

    fetchProjectData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.businessName || !formData.businessSummary) {
      setError("Please fill out all required fields.");
      toast.warn("Please complete all required fields.");
      setLoading(false);
      return;
    }

    try {
      if (!projectId) throw new Error("Project ID not set.");

      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        brandQuestionnaire: formData,
      });

      toast.success("Quesrionare responses saved successfully!");
      // navigate("/client");
    } catch (err) {
      setError("Failed to save responses: " + err.message);
      toast.error("Failed to save responses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-page">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Brand Questionnaire</h1>
        <p className="cp-subtitle">We've pre-filled some answers from earlier — feel free to add more detail.</p>
      </div>
      <form onSubmit={handleSubmit}>
        {/* Section: Your Business */}
        <div className="cp-section" style={{ marginBottom: '20px' }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Your Business</h2>
          </div>
          <div className="cp-section-body">
            <div className="cp-form-group">
              <label>What's the name of your business?</label>
              <input type="text" name="businessName" value={formData.businessName} onChange={handleChange} required />
            </div>
            <div className="cp-form-group">
              <label>Give a short summary of your business (1–2 sentences).</label>
              <textarea name="businessSummary" value={formData.businessSummary} onChange={handleChange} required></textarea>
            </div>
            <div className="cp-form-group">
              <label>Why does your business exist? (The story behind your business and core values.)</label>
              <textarea name="businessStory" value={formData.businessStory} onChange={handleChange}></textarea>
            </div>
            <div className="cp-form-group">
              <label>What problems are you solving for your customers?</label>
              <textarea name="customerProblems" value={formData.customerProblems} onChange={handleChange}></textarea>
            </div>
            <div className="cp-form-group">
              <label>Why do clients/customers choose you? What sets you apart?</label>
              <textarea name="uniqueSellingPoints" value={formData.uniqueSellingPoints} onChange={handleChange}></textarea>
            </div>
            <div className="cp-form-group">
              <label>What are your goals for the business over the next year?</label>
              <textarea name="businessGoals" value={formData.businessGoals} onChange={handleChange}></textarea>
            </div>
          </div>
        </div>

        {/* Section: Your Audience */}
        <div className="cp-section" style={{ marginBottom: '20px' }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Your Audience</h2>
          </div>
          <div className="cp-section-body">
            <div className="cp-form-group">
              <label>Who is your ideal customer?</label>
              <textarea name="idealCustomer" value={formData.idealCustomer} onChange={handleChange}></textarea>
            </div>
            <div className="cp-form-group">
              <label>What specific challenges does your audience face that your business helps solve?</label>
              <textarea name="audienceChallenges" value={formData.audienceChallenges} onChange={handleChange}></textarea>
            </div>
            <div className="cp-form-group">
              <label>What emotions or feelings do you want your audience to associate with your brand?</label>
              <textarea name="audienceEmotions" value={formData.audienceEmotions} onChange={handleChange}></textarea>
            </div>
            <div className="cp-form-group">
              <label>Do you have any competitors? (List them and explain how your business is different.)</label>
              <textarea name="competitors" value={formData.competitors} onChange={handleChange}></textarea>
            </div>
          </div>
        </div>

        {/* Section: Your Design */}
        <div className="cp-section" style={{ marginBottom: '20px' }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Your Design</h2>
          </div>
          <div className="cp-section-body">
            <div className="cp-form-group">
              <label>What are your preferred colors for your brand?</label>
              <input type="text" name="preferredColors" value={formData.preferredColors} onChange={handleChange} placeholder="E.g., Blue, White, Gold" />
            </div>
            <div className="cp-form-group">
              <label>Do you have any specific fonts or typography in mind for your branding?</label>
              <input type="text" name="preferredFonts" value={formData.preferredFonts} onChange={handleChange} />
            </div>
            <div className="cp-form-group">
              <label>What style best represents your brand? (E.g., modern, classic, playful, professional.)</label>
              <textarea name="brandStyle" value={formData.brandStyle} onChange={handleChange}></textarea>
            </div>
            <div className="cp-form-group">
              <label>Do you have any inspirational ideas, images, or references that represent your vision?</label>
              <textarea name="brandInspiration" value={formData.brandInspiration} onChange={handleChange}></textarea>
            </div>
            <div className="cp-form-group">
              <label>Any additional notes or ideas about how your brand should look and feel?</label>
              <textarea name="additionalNotes" value={formData.additionalNotes} onChange={handleChange}></textarea>
            </div>
          </div>
        </div>

        {error && <p className="cp-message--error">{error}</p>}
        <div className="cp-btn-row">
          <button type="submit" className="cp-btn-primary" disabled={loading || !projectId}>
            {loading ? 'Saving...' : 'Save Questionnaire'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BrandQuestionnaire;
